import MuiPagination from '@mui/material/Pagination'
import { Loading } from 'components'
import { translate } from 'locales'
import { formatLocal } from 'utils/date'
import React from 'react'
import {
  consultationStatusColor,
  consultationStatusIcon,
  consultationStatusLabelKey,
} from './consultationStatus'
import {
  IFinancialMonthAppointmentsMeta,
  IMonthlyConsultationRow,
} from '../types'
import { formatCurrency, getInitials } from '../utils'
import * as S from './styles'

interface ConsultationsTableProps {
  rows: IMonthlyConsultationRow[]
  meta: IFinancialMonthAppointmentsMeta
  locale: string
  loading: boolean
  isGeneralPractitioner: boolean
  onPageChange: (page: number) => void
}

const ConsultationsTable = ({
  rows,
  meta,
  locale,
  loading,
  isGeneralPractitioner,
  onPageChange,
}: ConsultationsTableProps) => {
  const rangeStart = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1
  const rangeEnd = Math.min(meta.page * meta.limit, meta.total)

  return (
    <S.TableSection>
      <S.TableHeader>
        <h2>{translate('financial.monthlyHistory.detail.tableTitle')}</h2>
        <span className="count">
          {meta.total}{' '}
          {translate('financial.monthlyHistory.detail.recordsSuffix')}
        </span>
      </S.TableHeader>

      <S.TableWrapper>
        {loading && <Loading title={translate('financial.loading')} />}

        <S.Table>
          <thead>
            <tr>
              <th>{translate('financial.monthlyHistory.detail.columnDate')}</th>
              <th>
                {translate('financial.monthlyHistory.detail.columnPatient')}
              </th>
              <th>
                {translate('financial.monthlyHistory.detail.columnSpecialty')}
              </th>
              <th>
                {translate('financial.monthlyHistory.detail.columnDuration')}
              </th>
              {!isGeneralPractitioner && (
                <th>
                  {translate('financial.monthlyHistory.detail.columnValue')}
                </th>
              )}
              <th>{translate('financial.monthlyHistory.detail.status')}</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              const StatusIcon = consultationStatusIcon(row.status)

              return (
                <tr key={row.id}>
                  <td>{formatLocal(row.datetime, 'dd MMM · HH:mm')}</td>
                  <td>
                    <S.PatientCell>
                      <S.PatientAvatar>
                        {getInitials(row.patientName)}
                      </S.PatientAvatar>
                      {row.patientName}
                    </S.PatientCell>
                  </td>
                  <td>
                    <S.SpecialtyTag>{row.specialty}</S.SpecialtyTag>
                  </td>
                  <td>{row.durationMinutes} min</td>
                  {!isGeneralPractitioner && (
                    <S.ValueCell>
                      {formatCurrency(Number(row.value) || 0, locale)}
                    </S.ValueCell>
                  )}
                  <td>
                    <S.StatusBadge color={consultationStatusColor(row.status)}>
                      <StatusIcon
                        style={{
                          fontSize: '1.6rem',
                          color: consultationStatusColor(row.status),
                        }}
                      />
                      {translate(consultationStatusLabelKey(row.status))}
                    </S.StatusBadge>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </S.Table>

        {!loading && rows.length === 0 && (
          <S.EmptyState>
            {translate('financial.monthlyHistory.detail.tableEmpty')}
          </S.EmptyState>
        )}
      </S.TableWrapper>

      {meta.total > 0 && (
        <S.TableFooter>
          <span className="showing">
            {translate('financial.monthlyHistory.detail.showing')} {rangeStart}-
            {rangeEnd} {translate('financial.monthlyHistory.detail.of')}{' '}
            {meta.total} {translate('financial.monthlyHistory.detail.records')}
          </span>

          {meta.totalPages > 1 && (
            <S.PaginationWrapper>
              <MuiPagination
                count={meta.totalPages}
                page={meta.page}
                onChange={(_, value) => onPageChange(value)}
              />
            </S.PaginationWrapper>
          )}
        </S.TableFooter>
      )}
    </S.TableSection>
  )
}

export default ConsultationsTable
